// Message reactions — pure helpers shared by the chat state (use-chat) and the
// bubble UI (MessageRow). The wire shape is the harness contract: each entry is
// one person's reaction; the caller's own entries carry `by: 'user'`.

import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { isPlaceholderAttachment } from './attachment-placeholder';

export type MessageReaction = { emoji: string; by: string; name?: string; at?: string };

/** The quick-react bar's six emoji, in display order (mirrored on native). The tool bar's emoji picker
 *  offers the same six. */
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;

/** When the picker's i-th emoji starts rising in, in ms: 60ms, then 45ms more per emoji. Shared 1:1 with
 *  native (which caps the step at the seventh cell; the picker only has six). */
export function reactionPickerDelay(index: number): number {
  return 60 + Math.min(index, 6) * 45;
}

/** The `by` value the harness stamps on the calling user's own reactions. */
export const OWN_REACTION_BY = 'user';

/** The `by` value the harness stamps on the assistant's own reactions (`ASSISTANT_REACTOR`). */
export const ASSISTANT_REACTION_BY = 'assistant';

/** True when the assistant reacted to this row — on a USER row a solo reaction is the whole reply. */
export function hasAssistantReaction(row: { reactions?: MessageReaction[] }): boolean {
  return (row.reactions ?? []).some((r) => r.by === ASSISTANT_REACTION_BY);
}

type ReconcileRow = { id?: string; finalId?: string; role?: string; content?: string; reactions?: MessageReaction[] };

// A row this tab sent whose server id it does not know yet — `use-chat` mints `optimistic-…` ids for them.
const isUnconfirmedSend = (row: ReconcileRow, text: string) =>
  row.role === 'USER' && !row.finalId && !!row.id?.startsWith('optimistic-') && row.content === text;

/**
 * The row this send persisted, when the turn answered it with a reaction alone — or undefined.
 *
 * A solo `ReactToMessage` turn writes no ASSISTANT row: the 👍 lands on the USER row the stream opened
 * with. So the post-stream reconcile, which waits for a NEW assistant row, never finds one, and the
 * duplexer republish of the reacted row lands while the stream is still open and is dropped. Without
 * this the reaction stayed invisible until a reload.
 *
 * The sent row is the OLDEST server USER row with the sent text that comes after the last row the client
 * already held (`preTurnRows`, the client's list at send time). The server persists it when the stream
 * opens, so a message another device sends during the turn — even with the same text — lands after it.
 * Any assistant row after it means the turn did reply, which the caller handles.
 *
 * One more case comes first: this tab's own earlier send of the same text, still holding only its
 * optimistic id (a quick repeat, before that send's reconcile stamped its server id). Its server row is
 * in the same window and comes before ours, so it is skipped. Only optimistic rows AFTER the last row both
 * sides know are counted — that is the window `fresh` is searched in.
 */
export function reactionAnsweredRow<M extends ReconcileRow>(fresh: M[], preTurnRows: ReconcileRow[], sentText: string): M | undefined {
  const knownIds = new Set(preTurnRows.flatMap((m) => [m.id, m.finalId]).filter((id): id is string => !!id));
  let start = 0;
  fresh.forEach((row, i) => {
    if (row.id && knownIds.has(row.id)) start = i + 1;
  });
  const boundaryId = start > 0 ? fresh[start - 1].id : undefined;
  const localBoundary = boundaryId ? preTurnRows.findIndex((m) => m.id === boundaryId || m.finalId === boundaryId) : -1;
  let toSkip = preTurnRows.slice(localBoundary + 1).filter((m) => isUnconfirmedSend(m, sentText)).length;
  const sentIndex = fresh.findIndex((row, i) => i >= start && row.role === 'USER' && row.content === sentText && toSkip-- === 0);
  if (sentIndex < 0) return undefined;
  if (fresh.slice(sentIndex + 1).some((row) => row.role === 'ASSISTANT' || row.role === 'ASSISTANT_STEP')) return undefined;
  return hasAssistantReaction(fresh[sentIndex]) ? fresh[sentIndex] : undefined;
}

/** Toggle one person's reaction: remove their existing entry for the emoji, else add one. */
export function toggleReaction(
  reactions: MessageReaction[] | undefined,
  emoji: string,
  by: string,
): MessageReaction[] {
  const list = reactions ?? [];
  const mine = (r: MessageReaction) => r.emoji === emoji && r.by === by;
  return list.some(mine) ? list.filter((r) => !mine(r)) : [...list, { emoji, by }];
}

type Reactable = { id?: string; finalId?: string; reactions?: MessageReaction[]; choices?: unknown; attachments?: MessageAttachment[] };

const matchesId = (m: Reactable, id: string) => m.id === id || m.finalId === id;

/**
 * Patch the matching row's attachments in place (matched by id OR persisted finalId). No-op when the
 * row is gone. Mirrors the native duplexer attachments patch; the SSE `messageEnd` in-band path is the
 * primary delivery, this covers other devices / async replies / other participants (same split as
 * choices).
 *
 * An EMPTY incoming list is normally ignored: a republish that dropped its attachments (a later
 * reaction toggle, say) must never erase already-rendered files. The exception is a row carrying
 * nothing but placeholders, where an empty list is the harness RETRACTING them — a second design brief
 * supersedes the first one's skeletons and `dropStalePlaceholders` says so by republishing the row with
 * no attachments. Narrowing the clear to all-placeholder rows keeps the original guard intact: a real
 * file can never be erased this way, because a row holding one is not all placeholders.
 */
export function patchAttachmentsById<M extends Reactable>(
  messages: M[],
  messageId: string,
  attachments: MessageAttachment[],
): M[] {
  if (!messages.some((m) => matchesId(m, messageId))) return messages;
  if (attachments.length === 0) {
    const row = messages.find((m) => matchesId(m, messageId))!;
    const existing = row.attachments ?? [];
    if (existing.length === 0 || !existing.every(isPlaceholderAttachment)) return messages;
  }
  return messages.map((m) => (matchesId(m, messageId) ? { ...m, attachments } : m));
}

/** Replace the matching row's reactions (matched by id OR persisted finalId); every other field — and every other row — is untouched. */
export function setReactionsById<M extends Reactable>(
  messages: M[],
  messageId: string,
  reactions: MessageReaction[],
): M[] {
  if (!messages.some((m) => matchesId(m, messageId))) return messages;
  return messages.map((m) => (matchesId(m, messageId) ? { ...m, reactions } : m));
}

/**
 * Duplexer upsert-by-id: the harness republishes the UPDATED message entity on a
 * reaction change, so an incoming entity whose id is already on screen (as the
 * row's id or its persisted finalId) patches that row's `reactions` — and its
 * `choices` / `attachments`, when the republished final assistant row carries a
 * structured ask or files the SSE stream couldn't deliver — in place; everything
 * else on the row (streamed content, animate, cards) is kept. A non-empty
 * `attachments` never clobbers with an empty one. An unknown id appends as a new
 * message, exactly like the old blind append.
 */
export function upsertMessageById<M extends Reactable>(messages: M[], incoming: M): M[] {
  const id = incoming.id;
  if (!id || !messages.some((m) => matchesId(m, id))) return [...messages, incoming];
  return messages.map((m) =>
    matchesId(m, id)
      ? {
          ...m,
          reactions: incoming.reactions ?? [],
          ...(incoming.choices !== undefined ? { choices: incoming.choices } : {}),
          ...(incoming.attachments && incoming.attachments.length > 0 ? { attachments: incoming.attachments } : {}),
        }
      : m,
  );
}

export type ReactionGroup = { emoji: string; count: number; mine: boolean; who: string[] };

/** Group raw entries into per-emoji chips (first-seen order): count, whether the user is in, and the display names for the tooltip. */
export function groupReactions(reactions: MessageReaction[] | undefined): ReactionGroup[] {
  const groups: ReactionGroup[] = [];
  for (const r of reactions ?? []) {
    let group = groups.find((g) => g.emoji === r.emoji);
    if (!group) {
      group = { emoji: r.emoji, count: 0, mine: false, who: [] };
      groups.push(group);
    }
    group.count += 1;
    if (r.by === OWN_REACTION_BY) group.mine = true;
    group.who.push(r.name || r.by);
  }
  return groups;
}
